import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, ElementRef } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { SharedService } from '../../services/shared.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-pharmacy',
  templateUrl: './pharmacy.component.html',
  styleUrls: ['./pharmacy.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class NavMenuPharmacyComponent implements OnInit, OnDestroy {

  constructor(
    private service: SharedService,
    private router: Router) { }

  @ViewChild('pharmacySearchInput', { static: false }) pharmacySearchInput: ElementRef;
  @ViewChild('pharmacySelect', { static: false }) pharmacySelect: MatSelect;
  @ViewChild('facilitySearchInput', { static: false }) facilitySearchInput: ElementRef;
  @ViewChild('facilitySelect', { static: false }) facilitySelect: MatSelect;

  pharmacies: any[] = [];
  filteredPharmacyOptions: any[] = [];
  facilityOptions: any[] = [];
  filteredFacilityOptions: any[] = [];
  pharmacyIdSelected: number;
  facilityIdSelected: number;
  pharmacySearchText = '';
  facilitySearchText = '';

  private pharmacyPanelEl: HTMLElement = null;
  private pharmacyPanelKeyHandler: any = null;
  private facilityPanelEl: HTMLElement = null;
  private facilityPanelKeyHandler: any = null;

  ngOnInit(): void {
    this.refreshFacilities();
    this.service.loadIsIpAddressRestriction();
  }

  ngOnDestroy(): void {
    this.detachPharmacyPanelKeys();
    this.detachFacilityPanelKeys();
  }

  refreshFacilities() {
    this.service.getPharmacies().subscribe(data => {
      this.pharmacies = data || [];
      this.pharmacySearchText = '';
      this.applyPharmacyFilter();

      if (this.pharmacies.length === 0) {
        this.facilityOptions = [];
        this.filteredFacilityOptions = [];
        return;
      }

      var pharmacy = this.findPharmacy(this.service.pharmacyId);
      if (!pharmacy) {
        pharmacy = this.pharmacies[0];
      }

      this.applyPharmacySelection(pharmacy);

      var facility = this.findFacility(pharmacy, this.service.facilityId);
      if (!facility && this.facilityOptions.length > 0) {
        facility = this.facilityOptions[0];
      }

      if (facility) {
        this.applyFacilitySelection(facility, false);
      }

      this.service.loadFacilities();
      this.syncSelectedIds();
    });
  }

  onPharmacyChange(pharmacyId: number) {
    var pharmacy = this.findPharmacy(pharmacyId);
    if (!pharmacy) {
      return;
    }

    this.applyPharmacySelection(pharmacy);

    var facility = this.facilityOptions.length > 0 ? this.facilityOptions[0] : null;
    if (facility) {
      this.applyFacilitySelection(facility, true);
    } else {
      this.facilityIdSelected = null;
    }

    this.service.loadFacilities();
    this.router.navigate(['/']);
  }

  onFacilityChange(facilityId: number) {
    var pharmacy = this.findPharmacy(this.pharmacyIdSelected);
    if (!pharmacy) {
      return;
    }

    var facility = this.findFacility(pharmacy, facilityId);
    if (!facility) {
      return;
    }

    this.applyFacilitySelection(facility, true);
    this.service.loadFacilities();
    this.router.navigate(['/']);
  }

  onPharmacyPanelOpened(opened: boolean) {
    if (!opened) {
      this.detachPharmacyPanelKeys();
      return;
    }

    this.pharmacySearchText = '';
    this.applyPharmacyFilter();

    setTimeout(() => {
      this.configureSelectKeyManager(this.pharmacySelect);
      this.focusPharmacySearch();
      this.attachPharmacyPanelKeys();
    }, 0);
  }

  onFacilityPanelOpened(opened: boolean) {
    if (!opened) {
      this.detachFacilityPanelKeys();
      return;
    }

    this.facilitySearchText = '';
    this.applyFacilityFilter();

    setTimeout(() => {
      this.configureSelectKeyManager(this.facilitySelect);
      this.focusFacilitySearch();
      this.attachFacilityPanelKeys();
    }, 0);
  }

  onPharmacySearchChange() {
    this.applyPharmacyFilter();
  }

  onFacilitySearchChange() {
    this.applyFacilityFilter();
  }

  onPharmacySearchKeydown(event: KeyboardEvent) {
    this.onSelectSearchKeydown(event, () => this.enterPharmacyListFromSearch());
  }

  onFacilitySearchKeydown(event: KeyboardEvent) {
    this.onSelectSearchKeydown(event, () => this.enterFacilityListFromSearch());
  }

  private onSelectSearchKeydown(event: KeyboardEvent, enterList: () => void) {
    var key = event.key || (event as any).keyIdentifier;

    if (key === 'ArrowDown' || key === 'Down') {
      event.preventDefault();
      event.stopPropagation();
      enterList();
      return;
    }

    if (key === ' ' || key === 'Spacebar' || key === 'Space') {
      event.stopPropagation();
      return;
    }

    // Typing filters the list; do not let mat-select typeahead jump options.
    if (key !== 'Escape' && key !== 'Tab' && key !== 'Enter') {
      event.stopPropagation();
    }
  }

  private onPharmacyPanelKeydown(event: KeyboardEvent) {
    this.onSearchablePanelKeydown(
      event,
      () => this.isPharmacySearchFocused(),
      () => this.enterPharmacyListFromSearch(),
      () => this.isFirstPharmacyOptionActive(),
      () => this.focusPharmacySearch(),
      (delta) => this.movePharmacyActive(delta)
    );
  }

  private onFacilityPanelKeydown(event: KeyboardEvent) {
    this.onSearchablePanelKeydown(
      event,
      () => this.isFacilitySearchFocused(),
      () => this.enterFacilityListFromSearch(),
      () => this.isFirstFacilityOptionActive(),
      () => this.focusFacilitySearch(),
      (delta) => this.moveFacilityActive(delta)
    );
  }

  private onSearchablePanelKeydown(
    event: KeyboardEvent,
    isSearchFocused: () => boolean,
    enterList: () => void,
    isFirstActive: () => boolean,
    focusSearch: () => void,
    moveActive: (delta: number) => void
  ) {
    var key = event.key || '';
    var isUp = key === 'ArrowUp' || key === 'Up';
    var isDown = key === 'ArrowDown' || key === 'Down';
    if (!isUp && !isDown) {
      return;
    }

    if (isSearchFocused()) {
      if (isDown) {
        this.stopKeyboardEvent(event);
        enterList();
      }
      return;
    }

    if (isUp && isFirstActive()) {
      this.stopKeyboardEvent(event);
      focusSearch();
      return;
    }

    this.stopKeyboardEvent(event);
    moveActive(isDown ? 1 : -1);
  }

  private stopKeyboardEvent(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }
  }

  private isPharmacySearchFocused(): boolean {
    return this.isSearchInputFocused(this.pharmacySearchInput);
  }

  private isFacilitySearchFocused(): boolean {
    return this.isSearchInputFocused(this.facilitySearchInput);
  }

  private isSearchInputFocused(inputRef: ElementRef): boolean {
    return !!(
      inputRef &&
      inputRef.nativeElement &&
      document.activeElement === inputRef.nativeElement
    );
  }

  private isFirstPharmacyOptionActive(): boolean {
    return this.isFirstEnabledOptionActive(
      this.pharmacySelect,
      this.pharmacyPanelEl
    );
  }

  private isFirstFacilityOptionActive(): boolean {
    return this.isFirstEnabledOptionActive(
      this.facilitySelect,
      this.facilityPanelEl
    );
  }

  private isFirstEnabledOptionActive(select: MatSelect, panel: HTMLElement): boolean {
    var enabled = this.getEnabledOptions(select);
    if (!enabled || enabled.length === 0) {
      return false;
    }

    var first = enabled[0];
    var selectAny = select as any;
    if (selectAny && selectAny._keyManager && selectAny._keyManager.activeItem) {
      return selectAny._keyManager.activeItem === first;
    }

    if (!panel) {
      return false;
    }

    var firstEl = panel.querySelector(
      '.mat-option:not(.select-search-option):not(.select-search-empty):not(.mat-option-disabled)'
    ) as HTMLElement;
    if (!firstEl) {
      return false;
    }

    var activeEl = panel.querySelector(
      '.mat-option.mat-active:not(.select-search-option):not(.mat-option-disabled)'
    ) as HTMLElement;
    if (activeEl && activeEl === firstEl) {
      return true;
    }

    return !!(
      document.activeElement &&
      (document.activeElement === firstEl || firstEl.contains(document.activeElement))
    );
  }

  private getEnabledPharmacyOptions(): any[] {
    return this.getEnabledOptions(this.pharmacySelect);
  }

  private getEnabledFacilityOptions(): any[] {
    return this.getEnabledOptions(this.facilitySelect);
  }

  private getEnabledOptions(select: MatSelect): any[] {
    if (!select || !select.options) {
      return [];
    }
    return select.options.toArray().filter(function (option) {
      return option && !option.disabled;
    });
  }

  private enterPharmacyListFromSearch() {
    this.enterListFromSearch(this.pharmacySearchInput, this.getEnabledPharmacyOptions(), this.pharmacySelect, this.pharmacyPanelEl);
  }

  private enterFacilityListFromSearch() {
    this.enterListFromSearch(this.facilitySearchInput, this.getEnabledFacilityOptions(), this.facilitySelect, this.facilityPanelEl);
  }

  private enterListFromSearch(
    searchInput: ElementRef,
    enabled: any[],
    select: MatSelect,
    panel: HTMLElement
  ) {
    if (searchInput && searchInput.nativeElement) {
      searchInput.nativeElement.blur();
    }
    if (!enabled.length) {
      return;
    }
    this.setActiveOption(enabled[0], select, panel);
  }

  private focusPharmacySearch() {
    this.focusSearch(this.pharmacySearchInput, this.pharmacySelect);
  }

  private focusFacilitySearch() {
    this.focusSearch(this.facilitySearchInput, this.facilitySelect);
  }

  private focusSearch(searchInput: ElementRef, select: MatSelect) {
    if (searchInput && searchInput.nativeElement) {
      searchInput.nativeElement.focus();
    }

    var selectAny = select as any;
    if (selectAny && selectAny._keyManager) {
      selectAny._keyManager.setActiveItem(-1);
    }
  }

  private movePharmacyActive(delta: number) {
    this.moveActive(delta, this.getEnabledPharmacyOptions(), this.pharmacySelect, this.pharmacyPanelEl);
  }

  private moveFacilityActive(delta: number) {
    this.moveActive(delta, this.getEnabledFacilityOptions(), this.facilitySelect, this.facilityPanelEl);
  }

  private moveActive(delta: number, enabled: any[], select: MatSelect, panel: HTMLElement) {
    if (!enabled.length) {
      return;
    }

    var currentIndex = this.getActiveOptionIndex(enabled, select);
    var nextIndex = currentIndex < 0 ? 0 : currentIndex + delta;
    if (nextIndex < 0) {
      nextIndex = 0;
    }
    if (nextIndex > enabled.length - 1) {
      nextIndex = enabled.length - 1;
    }

    this.setActiveOption(enabled[nextIndex], select, panel);
  }

  private getActiveOptionIndex(enabled: any[], select: MatSelect): number {
    var selectAny = select as any;
    var active = selectAny && selectAny._keyManager ? selectAny._keyManager.activeItem : null;
    if (!active) {
      return -1;
    }
    for (var i = 0; i < enabled.length; i++) {
      if (enabled[i] === active) {
        return i;
      }
    }
    return -1;
  }

  private setActiveOption(option: any, select: MatSelect, panel: HTMLElement) {
    if (!option) {
      return;
    }

    var selectAny = select as any;
    if (selectAny && selectAny._keyManager) {
      selectAny._keyManager.setActiveItem(option);
    }

    var host = option._getHostElement ? option._getHostElement() : null;
    if (host && typeof host.focus === 'function') {
      try {
        host.focus({ preventScroll: true });
      } catch (e) {
        host.focus();
      }
    }

    this.scrollOptionIntoView(panel, host || null);
  }

  private scrollOptionIntoView(panel: HTMLElement, optionEl: HTMLElement) {
    if (!panel || !optionEl) {
      return;
    }

    var searchEl = panel.querySelector('.select-search-option') as HTMLElement;
    var stickyTop = searchEl ? searchEl.getBoundingClientRect().height : 0;
    var panelRect = panel.getBoundingClientRect();
    var optionRect = optionEl.getBoundingClientRect();

    var visibleTop = panelRect.top + stickyTop;
    var visibleBottom = panelRect.bottom;

    if (optionRect.top < visibleTop) {
      panel.scrollTop = Math.max(0, panel.scrollTop - (visibleTop - optionRect.top));
      return;
    }

    if (optionRect.bottom > visibleBottom) {
      panel.scrollTop = Math.max(0, panel.scrollTop + (optionRect.bottom - visibleBottom));
    }
  }

  private configureSelectKeyManager(select: MatSelect) {
    var selectAny = select as any;
    if (!selectAny || !selectAny._keyManager) {
      return;
    }
    selectAny._keyManager.skipPredicate = function (item) {
      return !item || item.disabled;
    };
  }

  private attachPharmacyPanelKeys() {
    this.detachPharmacyPanelKeys();
    var panel = this.findPanel('.pharmacy-select-panel');
    if (!panel) {
      return;
    }
    this.pharmacyPanelEl = panel;
    var self = this;
    this.pharmacyPanelKeyHandler = function (event: KeyboardEvent) {
      self.onPharmacyPanelKeydown(event);
    };
    panel.addEventListener('keydown', this.pharmacyPanelKeyHandler, true);
  }

  private attachFacilityPanelKeys() {
    this.detachFacilityPanelKeys();
    var panel = this.findPanel('.facility-select-panel');
    if (!panel) {
      return;
    }
    this.facilityPanelEl = panel;
    var self = this;
    this.facilityPanelKeyHandler = function (event: KeyboardEvent) {
      self.onFacilityPanelKeydown(event);
    };
    panel.addEventListener('keydown', this.facilityPanelKeyHandler, true);
  }

  private findPanel(panelClass: string): HTMLElement {
    var panel = document.querySelector('.cdk-overlay-pane ' + panelClass) as HTMLElement;
    if (!panel) {
      panel = document.querySelector(panelClass) as HTMLElement;
    }
    return panel;
  }

  private detachPharmacyPanelKeys() {
    if (this.pharmacyPanelEl && this.pharmacyPanelKeyHandler) {
      this.pharmacyPanelEl.removeEventListener('keydown', this.pharmacyPanelKeyHandler, true);
    }
    this.pharmacyPanelEl = null;
    this.pharmacyPanelKeyHandler = null;
  }

  private detachFacilityPanelKeys() {
    if (this.facilityPanelEl && this.facilityPanelKeyHandler) {
      this.facilityPanelEl.removeEventListener('keydown', this.facilityPanelKeyHandler, true);
    }
    this.facilityPanelEl = null;
    this.facilityPanelKeyHandler = null;
  }

  private applyPharmacySelection(pharmacy: any) {
    this.service.loadPharmacyId(pharmacy.id);
    this.service.loadPharmacy(pharmacy.name);
    this.service.loadHasPharmacyPos(pharmacy.hasPos);
    this.facilityOptions = pharmacy.facilityVos || [];
    this.facilitySearchText = '';
    this.applyFacilityFilter();
    this.pharmacyIdSelected = pharmacy.id;
  }

  private applyFacilitySelection(facility: any, withSideEffects: boolean) {
    this.service.loadFacilityId(facility.id);
    this.service.setFacilityName(facility.name);
    this.service.loadIsFacilityPosOnly(facility.facilityTypeId);
    this.facilityIdSelected = facility.id;

    if (withSideEffects) {
      this.service.loadReceivingAlert();
    }
  }

  private applyPharmacyFilter() {
    var query = (this.pharmacySearchText || '').trim().toLowerCase();
    if (!query) {
      this.filteredPharmacyOptions = this.pharmacies.slice();
      return;
    }

    this.filteredPharmacyOptions = this.pharmacies.filter(function (pharmacy) {
      var name = pharmacy && pharmacy.name ? String(pharmacy.name) : '';
      return name.toLowerCase().indexOf(query) >= 0;
    });
  }

  private applyFacilityFilter() {
    var query = (this.facilitySearchText || '').trim().toLowerCase();
    if (!query) {
      this.filteredFacilityOptions = this.facilityOptions.slice();
      return;
    }

    this.filteredFacilityOptions = this.facilityOptions.filter(function (facility) {
      var name = facility && facility.name ? String(facility.name) : '';
      return name.toLowerCase().indexOf(query) >= 0;
    });
  }

  private findPharmacy(pharmacyId: number): any {
    if (pharmacyId == null || isNaN(pharmacyId) || !this.pharmacies) {
      return null;
    }
    return this.pharmacies.find(p => p.id == pharmacyId) || null;
  }

  private findFacility(pharmacy: any, facilityId: number): any {
    if (!pharmacy || !pharmacy.facilityVos || facilityId == null || isNaN(facilityId)) {
      return null;
    }
    return pharmacy.facilityVos.find(f => f.id == facilityId) || null;
  }

  private syncSelectedIds() {
    this.pharmacyIdSelected = this.service.pharmacyId;
    this.facilityIdSelected = this.service.facilityId;
  }
}
